import React, { Component } from 'react';

// Feature flags de producto. Vive en Configuración > Feature flags.
//
// Hasta acá estos flags sólo se podían tocar con `scripts/toggle-feature-flag.js`
// o SQL a mano contra la tabla `configuration`.
//
// El catálogo (etiqueta, descripción y alcance de cada flag) lo arma el backend y
// viaja en el GET: agregar un flag nuevo no requiere tocar este archivo.
const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

const ROLE_ADMIN = 1;

class FeatureFlagsSettingsComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            flags: [],          // catálogo del backend, con el valor guardado
            values: {},         // valor en pantalla (lo que el admin viene tocando)
            loading: true,
            saving: false,
            alert: null,        // { type, message }
        };
    }

    async componentDidMount() {
        await this.load();
    }

    fetchJson = async (path, options) => {
        const res = await fetch(apiBase() + path, { headers: authHeaders(), ...options });
        return res.json();
    }

    load = async () => {
        this.setState({ loading: true, alert: null });
        try {
            const body = await this.fetchJson('configuration/settings/feature-flags', { method: 'GET' });
            if (body.error !== 0) {
                this.setState({ loading: false, alert: { type: 'danger', message: body.message || 'No se pudieron cargar los feature flags' } });
                return;
            }
            this.applyServerState(body.data.flags, null);
        } catch (e) {
            this.setState({ loading: false, alert: { type: 'danger', message: 'No se pudieron cargar los feature flags' } });
        }
    }

    applyServerState = (flags, alert) => {
        this.setState({
            flags,
            values: flags.reduce((acc, flag) => {
                acc[flag.key] = flag.value;
                return acc;
            }, {}),
            loading: false,
            saving: false,
            alert,
        });
    }

    toggle = (key, value) => {
        this.setState((s) => ({ values: { ...s.values, [key]: value }, alert: null }));
    }

    // Sólo se mandan los flags que cambiaron: si otro admin tocó otro flag mientras
    // esta pantalla estaba abierta, no se lo pisamos con un valor viejo.
    pendingChanges = () => this.state.flags
        .filter((flag) => this.state.values[flag.key] !== flag.value)
        .reduce((acc, flag) => {
            acc[flag.key] = this.state.values[flag.key];
            return acc;
        }, {});

    save = async () => {
        const changes = this.pendingChanges();
        if (Object.keys(changes).length === 0) {
            return;
        }

        this.setState({ saving: true, alert: null });
        try {
            const body = await this.fetchJson('configuration/settings/feature-flags', {
                method: 'PUT',
                body: JSON.stringify({ flags: changes }),
            });

            if (body.error !== 0) {
                await this.reloadAfterFailure(this.describeError(body));
                return;
            }

            this.applyServerState(body.data.flags, { type: 'success', message: 'Cambios guardados.' });
        } catch (e) {
            await this.reloadAfterFailure('No se pudo confirmar el guardado.');
        }
    }

    describeError = (body) => {
        // 1002 acá casi nunca es "se venció la sesión": la pantalla se abrió con el
        // GET (rol 1 o 2) y el PUT es sólo para Administrador, y además el rol se
        // recalcula contra la sucursal elegida en el selector. Decir sólo "la sesión
        // expiró" manda al usuario a reloguearse para que le pase exactamente lo mismo.
        if (body.error === 1002) {
            return 'No se pudo guardar: necesitás ser Administrador en la sucursal seleccionada (o tu sesión venció).';
        }
        return body.data || body.message || 'No se pudieron guardar los cambios.';
    }

    // Tras un error NO se puede asumir que no se guardó nada, así que se vuelve a
    // leer del servidor en vez de dejar en pantalla el estado que el admin tocó.
    reloadAfterFailure = async (message) => {
        this.setState({ saving: false, alert: { type: 'danger', message } });
        try {
            const body = await this.fetchJson('configuration/settings/feature-flags', { method: 'GET' });
            if (body.error === 0) {
                this.applyServerState(body.data.flags, { type: 'danger', message: `${message} Abajo está el estado real guardado.` });
            }
        } catch (e) {
            // Si tampoco se puede releer, queda el mensaje de error y los toggles como
            // estaban: al menos no se afirma que se guardó.
        }
    }

    renderScope = (enforcement) => {
        if (enforcement === 'backend') {
            return <span className="badge badge-success">Bloquea la operación en el servidor</span>;
        }

        // Importante que se vea: apagar uno de estos NO cierra los endpoints, sólo
        // esconde la sección del sitio. No sirve como medida de seguridad.
        return <span className="badge badge-warning">Sólo oculta la sección del sitio</span>;
    }

    render() {
        if (this.state.loading) {
            return <p>Cargando…</p>;
        }

        const isAdmin = Number(this.props.role) === ROLE_ADMIN;
        const dirtyCount = Object.keys(this.pendingChanges()).length;

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">Feature flags</h6></div>
                <div className="card-body">
                    <p className="text-muted">
                        Prenden y apagan secciones del sitio público. Los cambios se aplican a todo el
                        sitio, no por sucursal, y el visitante los ve al recargar la página.
                    </p>

                    {this.state.alert && (
                        <div className={`alert alert-${this.state.alert.type}`}>{this.state.alert.message}</div>
                    )}

                    {!isAdmin && (
                        <div className="alert alert-info">
                            Sólo un Administrador puede modificar los feature flags.
                        </div>
                    )}

                    {this.state.flags.map((flag) => (
                        <div key={flag.key} className="d-flex align-items-start py-3 border-bottom">
                            <div className="custom-control custom-toggle mr-3" style={{ marginTop: '0.125rem' }}>
                                <input
                                    type="checkbox"
                                    id={`feature-flag-${flag.key}`}
                                    className="custom-control-input"
                                    checked={!!this.state.values[flag.key]}
                                    disabled={!isAdmin || this.state.saving}
                                    onChange={(e) => this.toggle(flag.key, e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor={`feature-flag-${flag.key}`}></label>
                            </div>
                            <div>
                                <label htmlFor={`feature-flag-${flag.key}`} className="m-0 font-weight-bold" style={{ cursor: isAdmin ? 'pointer' : 'default' }}>
                                    {flag.label}
                                </label>
                                {' '}
                                {this.renderScope(flag.enforcement)}
                                {this.state.values[flag.key] !== flag.value && (
                                    <span className="badge badge-light ml-1">sin guardar</span>
                                )}
                                <div className="text-muted"><small>{flag.description}</small></div>
                            </div>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="btn btn-primary mt-4"
                        disabled={!isAdmin || this.state.saving || dirtyCount === 0}
                        onClick={this.save}
                    >
                        {this.state.saving ? 'Guardando…' : 'Guardar'}
                    </button>
                    {dirtyCount > 0 && !this.state.saving && (
                        <span className="text-muted ml-2">
                            {dirtyCount} cambio{dirtyCount > 1 ? 's' : ''} sin guardar
                        </span>
                    )}
                </div>
            </div>
        );
    }
}

export default FeatureFlagsSettingsComponent;
